import { useState } from "react";

import { API_BASE_URL, ApiError, session, type SessionUser } from "./api";

/**
 * Connexion au back-office.
 *
 * Il n'y a pas d'inscription ici : les comptes se créent sur invitation, et un
 * organisateur reçoit la sienne. Proposer un formulaire d'inscription
 * laisserait croire le contraire.
 */
export function Login({ onSignedIn }: { onSignedIn: (user: SessionUser) => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const submit = async (event?: React.FormEvent) => {
    event?.preventDefault();
    if (busy) return;

    // Validation ici, et non par le navigateur.
    //
    // `required` bloquait la soumission sans que rien ne se passe à l'écran
    // lorsque la bulle du navigateur n'apparaissait pas : le bouton ne réagissait
    // pas, et il n'y avait aucun moyen de savoir pourquoi. Un message que l'on
    // écrit soi-même vaut mieux qu'un comportement qu'on ne maîtrise pas.
    if (!email.includes("@")) {
      setError("Renseignez une adresse électronique.");
      return;
    }
    if (password === "") {
      setError("Renseignez votre mot de passe.");
      return;
    }

    setBusy(true);
    setError(null);

    try {
      onSignedIn(await session.login(email, password));
    } catch (cause) {
      // Un message unique, quelle que soit la raison : distinguer « compte
      // inconnu » de « mot de passe faux » permettrait d'énumérer les comptes.
      setError(
        cause instanceof ApiError && cause.status === 401
          ? "Adresse ou mot de passe incorrect."
          : cause instanceof ApiError && cause.status === 429
            ? "Trop d'essais. Réessayez dans quelques minutes."
            : cause instanceof ApiError && cause.status === 0
              ? `Serveur injoignable depuis ce navigateur (${API_BASE_URL}). Rechargez la page ; si cela persiste, une extension bloque peut-être la requête.`
              : "Connexion impossible pour le moment.",
      );
    } finally {
      setBusy(false);
    }
  };

  if (resetting) {
    return (
      <ResetPassword
        initialEmail={email}
        onDone={(message) => {
          setResetting(false);
          setPassword("");
          setError(null);
          setNotice(message);
        }}
        onCancel={() => setResetting(false)}
      />
    );
  }

  return (
    <main style={{ maxWidth: 380, marginTop: "12vh" }}>
      <section className="card">
        <h2>Espace organisateur</h2>
        {notice ? <p className="badge">{notice}</p> : null}
        {/* `noValidate` : la validation est faite plus haut, en JavaScript. */}
        <form onSubmit={submit} noValidate style={{ display: "grid", gap: 12 }}>
          <label>
            Adresse électronique
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
            />
          </label>
          <label>
            Mot de passe
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              onKeyDown={(event) => {
                // Entrée valide le formulaire, comme on l'attend d'un champ de
                // mot de passe.
                if (event.key === "Enter") void submit();
              }}
            />
          </label>

          {error ? <p className="error">{error}</p> : null}

          {/* `onClick` en plus de la soumission : si celle-ci est empêchée par
              le navigateur, le bouton doit malgré tout agir. */}
          <button
            className="primary"
            type="submit"
            onClick={() => void submit()}
            disabled={busy}
          >
            {busy ? "Connexion…" : "Se connecter"}
          </button>
        </form>
        <button
          className="danger quiet"
          type="button"
          onClick={() => setResetting(true)}
        >
          Mot de passe oublié ?
        </button>
        <p className="muted">
          Les comptes sont créés sur invitation. Contactez l'administrateur si vous n'en
          avez pas.
        </p>
      </section>
    </main>
  );
}

/**
 * « Mot de passe oublié » : un code à six chiffres par e-mail, puis le nouveau
 * mot de passe. Toutes les sessions du compte sont fermées au passage.
 */
function ResetPassword({
  initialEmail,
  onDone,
  onCancel,
}: {
  initialEmail: string;
  onDone: (message: string) => void;
  onCancel: () => void;
}) {
  const [step, setStep] = useState<"request" | "confirm">("request");
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const run = async (action: () => Promise<void>, then: () => void) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      then();
    } catch (cause) {
      setError(
        cause instanceof ApiError && cause.code === "invalid_code"
          ? "Ce code n'est pas valable, ou il a expiré. Demandez-en un nouveau."
          : cause instanceof ApiError && cause.status === 429
            ? "Trop d'essais. Réessayez dans quelques minutes."
            : "Serveur injoignable. Réessayez dans un instant.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <main style={{ maxWidth: 380, marginTop: "12vh" }}>
      <section className="card">
        <h2>Mot de passe oublié</h2>
        {step === "request" ? (
          <>
            <p className="muted">
              Saisissez l'adresse de votre compte : nous y envoyons un code à six
              chiffres.
            </p>
            <label>
              Adresse électronique
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
              />
            </label>
            {error ? <p className="error">{error}</p> : null}
            <button
              className="primary"
              disabled={busy || !email.includes("@")}
              onClick={() =>
                void run(
                  () => session.requestReset(email.trim()),
                  () => setStep("confirm"),
                )
              }
            >
              Recevoir un code
            </button>
          </>
        ) : (
          <>
            <p className="badge">
              Si un compte utilise cette adresse, un code vient d'y être envoyé. Il est
              valable 15 minutes.
            </p>
            <label>
              Code reçu
              <input
                inputMode="numeric"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                autoComplete="one-time-code"
              />
            </label>
            <label>
              Nouveau mot de passe (douze caractères au minimum)
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
              />
            </label>
            {error ? <p className="error">{error}</p> : null}
            <button
              className="primary"
              disabled={busy || code.length !== 6 || password.length < 12}
              onClick={() =>
                void run(
                  () => session.confirmReset(email.trim(), code, password),
                  () => onDone("Mot de passe changé. Connectez-vous avec le nouveau."),
                )
              }
            >
              Changer le mot de passe
            </button>
          </>
        )}
        <button onClick={onCancel}>Retour à la connexion</button>
      </section>
    </main>
  );
}
